import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, CardBody, CardFooter, CardImg, CardSubtitle, CardTitle, Col, Row } from 'reactstrap';
import { IconArrowUpRight, IconBrandInstagram, IconCloudDownload } from '@tabler/icons-react';
import DynamicIcon from '../DynamicIcon/DynamicIcon.jsx';
import classes from './ImageCard.module.scss';

const renderCardIcon = (iconName) => {
    if (!iconName) return null;
    const lower = String(iconName).toLowerCase();
    if (lower.includes('instagram')) {
        return <IconBrandInstagram size={48} className="text-primary me-2" />;
    }
    return <DynamicIcon name={iconName} size={48} className="text-primary me-2" />;
};

const renderFooterIcon = (iconName) => {
    if (!iconName) return null;
    const lower = String(iconName).toLowerCase();
    if (lower.includes('download') || lower.includes('cloud')) {
        return <IconCloudDownload size={20} className="pe-1 me-1" />;
    }
    return <DynamicIcon name={iconName} size={20} className="pe-1 me-1" />;
};

const ImageCard = (props) => {
    const layout = props.variant || 'card';

    const imgUrl = props.assetUrl || '';
    const altText = props.altText || props.title || 'Platzhalter für das ECB-Feature';
    const targetUrl = props.link?.linkUrl || '';
    const linkTarget = props.link?.linkTarget || '_blank';

    const buttonLabel = props.link?.buttonText || '';
    const buttonPosition = props.buttonPosition || 'left';
    const isFullClickable = Boolean(props.isFullClickable);
    const buttonIcon = props.link?.buttonIcon || '';
    const buttonColor = props.link?.buttonColor || 'primary';

    const title = props.title || '';
    const description = props.description || '';
    const icon = props.link?.buttonIcon || '';

    if (layout === 'banner') {
        const smPos = buttonPosition === 'center' ? 'center' : 'fullwidth';
        const mdPos = buttonPosition;

        const smImgUrl = imgUrl || 'https://media.spendenwettbewerb.de/misc/socialvalue-bigfoot-ecb-placeholder-xs.svg';
        const xlImgUrl = imgUrl || 'https://media.spendenwettbewerb.de/misc/socialvalue-bigfoot-ecb-placeholder-xl.svg';

        const posSmClass = classes[`posSm_${smPos}`] || `ECB_posSm_${smPos}`;
        const posMdClass = classes[`posMd_${mdPos}`] || `ECB_posMd_${mdPos}`;

        return (
            <div className={classes.ECBCard} style={props.style}>
                <picture>
                    <source media="(max-width: 767px)" srcSet={smImgUrl} />
                    <source media="(min-width: 768px)" srcSet={xlImgUrl} />
                    <img src={xlImgUrl} alt={altText} className={`${classes.ECBImage} img-fluid`} />
                </picture>
                <div className={`${classes.ECBOverlay} ${posSmClass} ${posMdClass}`}>
                    {targetUrl && (
                        <Button
                            tag="a"
                            href={targetUrl}
                            target={linkTarget}
                            rel="noopener noreferrer"
                            color={buttonColor}
                            className={`${classes.ECBBtn} text-dark`}
                        >
                            {buttonLabel || 'Jetzt Chance auf 1 von 10 E-Bikes sichern!'}
                            <IconArrowUpRight size={20} className="ms-1" />
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    const isRichCard = Boolean(title || description);

    if (!isRichCard) {
        return (
            <Card className={classes.ECBCard} style={props.style}>
                {targetUrl || isFullClickable ? (
                    <a href={targetUrl} target={linkTarget} rel="noopener noreferrer">
                        <CardImg
                            top
                            src={imgUrl || 'https://placeholdit.com/600x600/428cd8/ffffff?text=ECB+Image'}
                            alt={altText || 'Platzhalter für die Embedded Customer Branding-Komponente (kurz ECB)'}
                            className={classes.ECBImage}
                        />
                    </a>
                ) : (
                    <CardImg
                        top
                        src={imgUrl || 'https://placeholdit.com/600x600/428cd8/ffffff?text=ECB+Image'}
                        alt={altText || 'Platzhalter für die Embedded Customer Branding-Komponente (kurz ECB)'}
                        className={classes.ECBImage}
                    />
                )}
            </Card>
        );
    }

    return (
        <Card className="h-100" style={props.style}>
            {imgUrl && <CardImg top src={imgUrl} alt={altText || title || ''} className="img-fluid" />}
            <CardBody>
                <Row>
                    {icon ? (
                        <>
                            <Col xs="3" className="pe-0">
                                {renderCardIcon(icon)}
                            </Col>
                            <Col xs="9" className="ps-0">
                                {title && <CardTitle tag="h5">{title}</CardTitle>}
                                {description && (
                                    <CardSubtitle tag="h6" className="mb-2 text-muted">
                                        {description}
                                    </CardSubtitle>
                                )}
                            </Col>
                        </>
                    ) : (
                        <Col xs="12">
                            {title && <CardTitle tag="h5">{title}</CardTitle>}
                            {description && (
                                <CardSubtitle tag="h6" className="mb-2 text-muted">
                                    {description}
                                </CardSubtitle>
                            )}
                        </Col>
                    )}
                </Row>
            </CardBody>
            {(buttonLabel || targetUrl) && (
                <CardFooter>
                    <Button
                        tag="a"
                        color={buttonColor}
                        className="w-100"
                        rel="external noopener"
                        href={targetUrl || '#'}
                        target={linkTarget}
                    >
                        {renderFooterIcon(buttonIcon || icon)}
                        {buttonLabel || 'Paket herunterladen'}
                    </Button>
                </CardFooter>
            )}
        </Card>
    );
};

ImageCard.propTypes = {
    variant: PropTypes.string,
    assetUrl: PropTypes.string,
    altText: PropTypes.string,
    buttonPosition: PropTypes.oneOf(['left', 'center', 'right']),
    isFullClickable: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    link: PropTypes.shape({
        linkUrl: PropTypes.string,
        linkTarget: PropTypes.string,
        buttonText: PropTypes.string,
        buttonIcon: PropTypes.string,
        buttonColor: PropTypes.string,
    }),
    style: PropTypes.object,
};

export default ImageCard;
